import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"
import {
  getDaysBetween,
  getDaysUntil,
  getTrialFacts,
  type TrialFacts,
} from "@/v2-backoffice-client/license/license.utils"
import type { PortfolioCustomer } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  deriveUsageScopeSummary,
  type UsageScopeMember,
  type UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  TRIAL_ENDING_SOON_DAYS,
  TRIAL_NEW_DAYS,
  type TrialClockState,
  type TrialPulse,
} from "@/v2-backoffice-client/license/trials/license.trials.constants"

// The trials report's model. One row per trialing CUSTOMER (a customer can
// hold several trial licenses — Placepoint and an MCP server, say), built
// from the trial licenses (getLicenses with status Trial: every product,
// the seated MCP servers included, which the customers board's governing
// status leaves out) joined with the customer's seats and their usage
// profiles from the portfolio read. Pure derivation: the same scope summary
// the customer panel's Activity tab computes, so the two agree.

/** One trial license as the report reads it. */
export type TrialLicense = {
  id: number
  productCode: number
  productName: string
  planName: string | null
  licenseSourceCode: number
  /** YYYY-MM-DD, or null when the license carries no start date. */
  startedAt: string | null
  /** YYYY-MM-DD, or null for an open-ended trial. */
  endsAt: string | null
  /** The countdown; null without an end date. */
  facts: TrialFacts | null
  seats: number | null
  /** The product assigns seats to named people (the MCP servers). */
  seatsAreAssigned: boolean
  seatsAssigned: number
}

/** The clock that decides first among a customer's trials. */
export type TrialClock = {
  startedAt: string | null
  endsAt: string
  facts: TrialFacts
  totalDays: number | null
  /** Share of the trial elapsed, 0–1; null without a start date. */
  progress: number | null
}

/** A member of a trialing customer, with which trial seats they hold. */
export type TrialMember = UsageScopeMember & {
  /** Names of the seated trial products this person holds a seat on. */
  seatProducts: string[]
}

export type TrialCustomer = {
  customerId: number
  /** The HubSpot company id the panels are keyed by; null until linked. */
  externalCustomerId: string | null
  name: string
  /** HubSpot company owner's display name — whose book the trial is in. */
  owner: string | null
  licenses: TrialLicense[]
  /** Distinct product names on trial, in license order. */
  products: string[]
  clock: TrialClock | null
  clockState: TrialClockState
  /** The earliest start among the licenses. */
  startedAt: string | null
  /** Started within {@link TRIAL_NEW_DAYS}. */
  isNew: boolean
  /** Summed seat caps of the trial licenses; null when none is capped. */
  licensedSeats: number | null
  members: TrialMember[]
  summary: UsageScopeSummary
  pulse: TrialPulse
  /** Members' day strips summed — the customer's 28-day shape, oldest first. */
  daily28d: number[]
}

type SeatHolderRef = { LicenseId: number; UserId: number }

const DAYS_IN_STRIP = 28

function toDateString(value: string | Date | null | undefined) {
  if (value == null) return null
  return String(value).slice(0, 10)
}

function toTrialLicense(license: LicenseFragment): TrialLicense {
  return {
    id: license.Id,
    productCode: license.ProductCode,
    productName: license.ProductName,
    planName: license.PlanName ?? null,
    licenseSourceCode: license.LicenseSourceCode,
    startedAt: toDateString(license.StartedAt),
    endsAt: toDateString(license.TrialEndsAt),
    facts: getTrialFacts(license),
    seats: license.Seats ?? null,
    seatsAreAssigned: license.SeatsAreAssigned,
    seatsAssigned: license.SeatsAssigned,
  }
}

/**
 * The clock that decides first: the soonest end date among the licenses that
 * have one. Progress is measured from that license's own start.
 */
function deriveClock(licenses: TrialLicense[]): TrialClock | null {
  const dated = licenses
    .filter(
      (
        license,
      ): license is TrialLicense & { endsAt: string; facts: TrialFacts } =>
        license.endsAt != null && license.facts != null,
    )
    .sort((a, b) => a.facts.daysLeft - b.facts.daysLeft)
  const soonest = dated[0]
  if (!soonest) return null

  const totalDays =
    soonest.startedAt != null
      ? getDaysBetween(soonest.startedAt, soonest.endsAt)
      : null
  const progress =
    totalDays != null && totalDays > 0
      ? Math.min(1, Math.max(0, 1 - soonest.facts.daysLeft / totalDays))
      : null

  return {
    startedAt: soonest.startedAt,
    endsAt: soonest.endsAt,
    facts: soonest.facts,
    totalDays,
    progress,
  }
}

function deriveClockState(
  clock: TrialClock | null,
  startedAt: string | null,
): TrialClockState {
  if (startedAt != null && getDaysUntil(startedAt) > 0) return "not-started"
  if (clock == null) return "open-ended"
  if (clock.facts.isOverdue) return "overdue"
  if (clock.facts.daysLeft <= TRIAL_ENDING_SOON_DAYS) return "ending-soon"
  return "running"
}

function derivePulse(summary: UsageScopeSummary): TrialPulse {
  if (summary.seatCount === 0) return "no-users"
  if (summary.activeUsers7d > 0) return "engaged"
  if (summary.activeUsers28d > 0) return "cooling"
  return "silent"
}

/** Members' 28 daily totals summed per day (missing strips count as zero). */
function sumDailyStrips(members: { Daily28d?: number[] }[]) {
  const series = Array.from({ length: DAYS_IN_STRIP }, () => 0)
  for (const member of members) {
    const strip = member.Daily28d ?? []
    // Strips are oldest first; align their ends in case one is shorter.
    const offset = DAYS_IN_STRIP - strip.length
    for (let index = 0; index < strip.length; index += 1) {
      const day = index + offset
      if (day >= 0 && day < DAYS_IN_STRIP) series[day] += strip[index]
    }
  }
  return series
}

/**
 * Builds the report's rows: the trial licenses grouped per customer, each
 * with its seats and their usage. A customer with trial licenses but no row
 * in the portfolio read has no users yet and still appears — that is a
 * finding in itself. Soonest-deciding clock first, open-ended trials last.
 */
export function deriveTrialCustomers(input: {
  licenses: LicenseFragment[]
  portfolio: PortfolioCustomer[]
  seatHolders: SeatHolderRef[]
}): TrialCustomer[] {
  const { licenses, portfolio, seatHolders } = input

  const membersByCustomer = new Map(
    portfolio.map((customer) => [
      customer.ExternalCustomerId,
      customer.Members as UsageScopeMember[],
    ]),
  )
  const ownerByCustomer = new Map(
    portfolio.map((customer) => [
      customer.ExternalCustomerId,
      customer.Owner ?? null,
    ]),
  )
  // Named seat holders per license, so a seated trial (an MCP pilot) can
  // say who is actually on it.
  const holdersByLicense = new Map<number, Set<number>>()
  for (const holder of seatHolders) {
    const set = holdersByLicense.get(holder.LicenseId) ?? new Set<number>()
    set.add(holder.UserId)
    holdersByLicense.set(holder.LicenseId, set)
  }

  // Grouped by the customer's own id: an app-created customer has no
  // HubSpot id yet, and its trial still counts.
  const byCustomer = new Map<number, LicenseFragment[]>()
  for (const license of licenses) {
    const group = byCustomer.get(license.CustomerId) ?? []
    group.push(license)
    byCustomer.set(license.CustomerId, group)
  }

  const customers: TrialCustomer[] = []
  for (const [customerId, group] of byCustomer) {
    const externalCustomerId = group[0].ExternalCustomerId ?? null
    const trialLicenses = group.map(toTrialLicense)
    const rawMembers =
      (externalCustomerId != null
        ? membersByCustomer.get(externalCustomerId)
        : undefined) ?? []
    const members: TrialMember[] = rawMembers.map((member) => ({
      ...member,
      seatProducts: trialLicenses
        .filter(
          (license) =>
            license.seatsAreAssigned &&
            holdersByLicense.get(license.id)?.has(member.UserId),
        )
        .map((license) => license.productName),
    }))
    const summary = deriveUsageScopeSummary(members)
    const clock = deriveClock(trialLicenses)
    const startedAt =
      trialLicenses
        .map((license) => license.startedAt)
        .filter((date): date is string => date != null)
        .sort()[0] ?? null
    const capped = trialLicenses.filter((license) => license.seats != null)
    // Days since the trial began; negative while the start is still ahead.
    const daysSinceStart = startedAt != null ? -getDaysUntil(startedAt) : null

    customers.push({
      customerId,
      externalCustomerId,
      name: group[0].CustomerName ?? `Customer ${customerId}`,
      owner:
        group[0].OwnerName ??
        (externalCustomerId != null
          ? ownerByCustomer.get(externalCustomerId)
          : null) ??
        null,
      licenses: trialLicenses,
      products: [
        ...new Set(trialLicenses.map((license) => license.productName)),
      ],
      clock,
      clockState: deriveClockState(clock, startedAt),
      startedAt,
      isNew:
        daysSinceStart != null &&
        daysSinceStart >= 0 &&
        daysSinceStart <= TRIAL_NEW_DAYS,
      licensedSeats:
        capped.length > 0
          ? capped.reduce((total, license) => total + (license.seats ?? 0), 0)
          : null,
      members,
      summary,
      pulse: derivePulse(summary),
      daily28d: sumDailyStrips(members),
    })
  }

  return customers.sort((a, b) => {
    const left = a.clock?.facts.daysLeft ?? Number.POSITIVE_INFINITY
    const right = b.clock?.facts.daysLeft ?? Number.POSITIVE_INFINITY
    if (left !== right) return left - right
    return a.name.localeCompare(b.name)
  })
}

/** The report's key figures — the weekly post's numbers, live. */
export type TrialKpis = {
  customerCount: number
  licenseCount: number
  /** License count per product name, most first. */
  byProduct: { product: string; count: number }[]
  /** People with a seat at a trialing customer. */
  peopleCount: number
  activePeople7d: number
  activePeople28d: number
  /** People with no tracked activity in the last 28 days. */
  silentPeople: number
  engagedCustomers: number
  silentCustomers: number
  endingSoon: number
  overdue: number
  newThisWeek: number
}

export function deriveTrialKpis(customers: TrialCustomer[]): TrialKpis {
  const productCounts = new Map<string, number>()
  const kpis: TrialKpis = {
    customerCount: customers.length,
    licenseCount: 0,
    byProduct: [],
    peopleCount: 0,
    activePeople7d: 0,
    activePeople28d: 0,
    silentPeople: 0,
    engagedCustomers: 0,
    silentCustomers: 0,
    endingSoon: 0,
    overdue: 0,
    newThisWeek: 0,
  }
  for (const customer of customers) {
    kpis.licenseCount += customer.licenses.length
    for (const license of customer.licenses) {
      productCounts.set(
        license.productName,
        (productCounts.get(license.productName) ?? 0) + 1,
      )
    }
    kpis.peopleCount += customer.summary.seatCount
    kpis.activePeople7d += customer.summary.activeUsers7d
    kpis.activePeople28d += customer.summary.activeUsers28d
    kpis.silentPeople +=
      customer.summary.seatCount - customer.summary.activeUsers28d
    if (customer.pulse === "engaged") kpis.engagedCustomers += 1
    if (customer.pulse === "silent" || customer.pulse === "no-users") {
      kpis.silentCustomers += 1
    }
    if (customer.clockState === "ending-soon") kpis.endingSoon += 1
    if (customer.clockState === "overdue") kpis.overdue += 1
    if (customer.isNew) kpis.newThisWeek += 1
  }
  kpis.byProduct = [...productCounts.entries()]
    .map(([product, count]) => ({ product, count }))
    .sort((a, b) => b.count - a.count || a.product.localeCompare(b.product))
  return kpis
}

/** Display name for a member, falling back to the email, then the id. */
export function formatTrialMemberName(member: TrialMember) {
  const name = [member.FirstName, member.LastName]
    .filter((part) => part && part.trim() !== "")
    .join(" ")
  return name || member.Email || `User ${member.UserId}`
}
